import { useState } from "react";
import { Activity, CalendarEvent, Wellness } from "../types/training";
import { calcIntensityRatio } from "./planGenerator";
import { eventHardTotalMins } from "./chartMetrics";

// ── ISO week helpers ──────────────────────────────────────────────────────────

function isoWeekKey(dateStr: string): string {
  const d = new Date(dateStr.slice(0, 10) + "T12:00:00");
  const utc = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((utc.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${utc.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

function weekLabel(key: string): string {
  const m = key.match(/W(\d+)$/);
  return m ? `V${parseInt(m[1])}` : key;
}

function buildWeekKeys(today: Date, past: number, future: number): string[] {
  const keys: string[] = [];
  for (let i = -past; i <= future; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i * 7);
    const k = isoWeekKey(d.toISOString().slice(0, 10));
    if (!keys.includes(k)) keys.push(k);
  }
  return keys.sort();
}

// ── Chart data ────────────────────────────────────────────────────────────────

interface WeekData {
  key: string;
  label: string;
  actualPct:      number | null;
  plannedPct:     number | null;
  actualHardMins: number;
  actualTotalMins: number;
  plannedHardMins: number;
  plannedTotalMins: number;
  isCurrent: boolean;
  isPast:    boolean;
}

function buildWeekData(
  activities: Activity[],
  events: CalendarEvent[],
  weeksBack: number,
  weeksFwd: number,
): WeekData[] {
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const currentKey = isoWeekKey(todayStr);
  const keys = buildWeekKeys(today, weeksBack, weeksFwd);

  type Bucket = { aHard: number; aTotal: number; pHard: number; pTotal: number };
  const buckets: Record<string, Bucket> = {};
  keys.forEach(k => { buckets[k] = { aHard: 0, aTotal: 0, pHard: 0, pTotal: 0 }; });

  // Actual zone times from completed activities
  for (const a of activities) {
    if (!a.moving_time || a.moving_time < 540) continue;
    const k = isoWeekKey(a.start_date_local);
    if (!buckets[k]) continue;
    const zones = a.icu_hr_zone_times;
    if (!zones || zones.length < 5) continue;
    const totalZoneSecs = zones.reduce((s, v) => s + v, 0);
    if (totalZoneSecs < 60) continue;
    buckets[k].aTotal += totalZoneSecs / 60;
    buckets[k].aHard  += zones.slice(3).reduce((s, v) => s + v, 0) / 60;
  }

  // Planned from events — only for current + future weeks
  for (const e of events) {
    const eDate = e.start_date_local.slice(0, 10);
    const k = isoWeekKey(eDate);
    if (!buckets[k]) continue;
    if (eDate < todayStr) continue; // skip past events
    const { hard, total } = eventHardTotalMins(e);
    if (total <= 0) continue;
    buckets[k].pHard  += hard;
    buckets[k].pTotal += total;
  }

  return keys.map(k => {
    const b = buckets[k];
    const isCurrent = k === currentKey;
    // % isn't additive, so we don't stack like the TSS chart. Instead the current
    // week's "planned" bar is the FULL-WEEK projection — what's been done PLUS
    // what's planned — so it's comparable to a completed week rather than showing
    // only the intensity of the few remaining sessions.
    const pHard  = isCurrent ? b.aHard + b.pHard : b.pHard;
    const pTotal = isCurrent ? b.aTotal + b.pTotal : b.pTotal;
    return {
      key:             k,
      label:           weekLabel(k),
      actualPct:       b.aTotal > 0 ? (b.aHard / b.aTotal) * 100 : null,
      plannedPct:      pTotal > 0 ? (pHard / pTotal) * 100 : null,
      actualHardMins:  b.aHard,
      actualTotalMins: b.aTotal,
      plannedHardMins: pHard,
      plannedTotalMins: pTotal,
      isCurrent,
      isPast:    k < currentKey,
    };
  });
}

function fmtMins(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

// ── SVG chart ─────────────────────────────────────────────────────────────────

const VW = 800;
const VH = 140;
const PAD_L = 4; const PAD_R = 4; const PAD_T = 16; const PAD_B = 20;
const CHART_W = VW - PAD_L - PAD_R;
const CHART_H = VH - PAD_T - PAD_B;
const MAX_PCT = 40;

interface TooltipState { x: number; y: number; week: WeekData; }

export function PolarizationChart({ activities, events, wellness }: {
  activities: Activity[];
  events: CalendarEvent[];
  wellness: Wellness;
}) {
  const [tip, setTip] = useState<TooltipState | null>(null);

  const weeks = buildWeekData(activities, events, 8, 8);
  const n = weeks.length;
  const colW = CHART_W / n;
  const barW = colW * 0.55;
  const bx  = (i: number) => PAD_L + i * colW + (colW - barW) / 2;
  const pctY = (pct: number) => PAD_T + CHART_H - (Math.min(pct, MAX_PCT) / MAX_PCT) * CHART_H;
  const pctH = (pct: number) => (Math.min(pct, MAX_PCT) / MAX_PCT) * CHART_H;

  const ctl = wellness.ctl ?? 50;
  const tsb = wellness.ctl != null && wellness.atl != null ? wellness.ctl - wellness.atl : null;
  const optPct = calcIntensityRatio(ctl, tsb) * 100;
  const optY   = pctY(optPct);

  return (
    <div className="pol-chart" onMouseLeave={() => setTip(null)}>
      <svg
        viewBox={`0 0 ${VW} ${VH}`}
        style={{ width: "100%", height: VH, display: "block" }}
        onMouseLeave={() => setTip(null)}
      >
        <line x1={PAD_L} y1={PAD_T + CHART_H} x2={VW - PAD_R} y2={PAD_T + CHART_H}
          stroke="rgba(255,255,255,0.07)" strokeWidth={1} />

        {weeks.map((w, i) => {
          const x = bx(i);
          const hasActual  = w.actualPct !== null;
          const hasPlanned = w.plannedPct !== null;
          const opacity    = w.isCurrent ? 0.6 : 1;

          const actualColor = (w.actualPct ?? 0) > optPct
            ? "rgba(220,80,80,0.75)"
            : "rgba(80,200,120,0.65)";
          const plannedStroke = (w.plannedPct ?? 0) > optPct
            ? "rgba(220,80,80,0.65)"
            : "rgba(255,255,255,0.32)";

          return (
            <g key={w.key}
              onMouseEnter={e => {
                const svg = e.currentTarget.ownerSVGElement as SVGSVGElement;
                const rect = svg.getBoundingClientRect();
                const cx = rect.left + (x + barW / 2) / VW * rect.width;
                const cy = rect.top + PAD_T / VH * rect.height;
                setTip({ x: cx, y: cy, week: w });
              }}
            >
              {/* Actual bar — solid */}
              {hasActual && (
                <rect x={x} y={pctY(w.actualPct!)} width={barW} height={pctH(w.actualPct!)}
                  fill={actualColor} opacity={opacity} rx={2} />
              )}

              {/* Planned bar — ghost outline */}
              {hasPlanned && !w.isPast && (
                <rect x={x} y={pctY(w.plannedPct!)} width={barW} height={pctH(w.plannedPct!)}
                  fill="rgba(255,255,255,0.07)"
                  stroke={plannedStroke} strokeWidth={1}
                  rx={2} />
              )}

              <text x={x + barW / 2} y={VH - 3} textAnchor="middle"
                fontSize={w.isCurrent ? 9 : 8} fontWeight={w.isCurrent ? 700 : 400}
                fill={w.isCurrent ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.3)"}
                fontFamily="inherit">
                {w.label}
              </text>

              {w.isCurrent && (
                <line x1={x + barW / 2} y1={PAD_T} x2={x + barW / 2} y2={PAD_T + CHART_H}
                  stroke="rgba(255,255,255,0.12)" strokeWidth={1} strokeDasharray="3 3" />
              )}
            </g>
          );
        })}

        {/* Optimal % line */}
        <g>
          <line x1={PAD_L} y1={optY} x2={VW - PAD_R} y2={optY}
            stroke="rgba(220,80,80,0.45)" strokeWidth={1} strokeDasharray="4 4" />
          <text x={VW - PAD_R - 2} y={optY - 3} textAnchor="end"
            fontSize={7} fill="rgba(220,80,80,0.6)" fontFamily="inherit">
            OPT {Math.round(optPct)}%
          </text>
        </g>
      </svg>

      {tip && (tip.week.actualPct !== null || tip.week.plannedPct !== null) && (
        <div className="tss-chart__tooltip" style={{ left: tip.x, top: tip.y }}>
          <div className="tss-chart__tooltip-label">{tip.week.label}</div>
          {tip.week.actualPct !== null && (
            <div>
              Genomfört: <strong>{Math.round(tip.week.actualPct)}%</strong>
              {" · "}{fmtMins(tip.week.actualHardMins)} av {fmtMins(tip.week.actualTotalMins)}
            </div>
          )}
          {tip.week.plannedPct !== null && !tip.week.isPast && (
            <div className="tss-chart__tooltip-planned">
              Planerat: <strong>{Math.round(tip.week.plannedPct)}%</strong>
              {" · "}{fmtMins(tip.week.plannedHardMins)} av {fmtMins(tip.week.plannedTotalMins)}
            </div>
          )}
          <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.75em", marginTop: 2 }}>
            opt {Math.round(optPct)}% Z4+
          </div>
        </div>
      )}

      <div className="tss-chart__legend">
        <span className="tss-chart__legend-item tss-chart__legend-item--actual">Genomfört Z4+%</span>
        <span className="tss-chart__legend-item tss-chart__legend-item--planned">Planerat Z4+% (est.)</span>
        <span className="tss-chart__legend-sep" />
        <span style={{ fontSize: "0.65rem", color: "rgba(220,80,80,0.6)", fontWeight: 600 }}>
          --- Optimal {Math.round(optPct)}%
        </span>
      </div>
    </div>
  );
}
